import { ref, computed, getCurrentInstance, Ref } from 'vue'
import { useStore } from 'vuex'
import apiUseCase from '@/usecase/apiUseCase'
import moment from 'moment'

export interface IPartnerSummary {
  PartnerId: string
  Name: string
  MsBankName: string
  AccountNumber: string
  AccountName: string
  TotalAmount: number
}

export function useBatchPaymentConfirm(
  dataSource: Ref<any[]>,
  selectedTransactionIds: Ref<Set<string>>
) {
  const store = useStore()
  const app = getCurrentInstance()
  // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
  const { $toast } = app!.appContext.config.globalProperties

  const isModalOpen = ref(false)
  const isLoadingPartnerSummary = ref(false)
  const isSubmitting = ref(false)
  const partnerSummary = ref<IPartnerSummary[]>([])

  const grandTotal = computed(() => partnerSummary.value.reduce((acc, curr) => acc + curr.TotalAmount, 0))

  const openConfirmModal = async () => {
    if (selectedTransactionIds.value.size === 0) return

    isLoadingPartnerSummary.value = true
    isModalOpen.value = true
    partnerSummary.value = []

    try {
      // 1. Filter selected transactions from dataSource
      const selectedTxs = dataSource.value.filter((item: any) => selectedTransactionIds.value.has(item.Id))

      // 2. Group by FuelStationPartnerId and calculate total price per partner
      const partnerTotalsMap = new Map<string, { partnerId: string; partnerName: string; total: number }>()

      selectedTxs.forEach((tx: any) => {
        const partnerId = tx.FuelStationPartnerId
        const price = tx.Price || 0
        const partnerName = tx.FuelStationPartnerName || ''

        const existing = partnerTotalsMap.get(partnerId)
        if (existing) {
          existing.total += price
        } else {
          partnerTotalsMap.set(partnerId, {
            partnerId,
            partnerName,
            total: price
          })
        }
      })

      // 3. Fetch detail for each unique partner
      const partnerIds = Array.from(partnerTotalsMap.keys())
      const partnerFetchPromises = partnerIds.map(async (id) => {
        const partnerInfo = partnerTotalsMap.get(id)
        const fallbackName = partnerInfo ? partnerInfo.partnerName : ''
        const totalAmt = partnerInfo ? partnerInfo.total : 0

        try {
          const response = await apiUseCase.get(`/management/v2/FuelStationPartners/${id}`)
          if (!response.error && response.result) {
            const data = response.result
            return {
              PartnerId: id,
              Name: data.Name || fallbackName || '-',
              MsBankName: data.MsBankName || '-',
              AccountNumber: data.AccountNumber || '-',
              AccountName: data.AccountName || '-',
              TotalAmount: totalAmt
            }
          }
        } catch (err) {
          // fetch error
        }
        // Fallback if fetch fails
        return {
          PartnerId: id,
          Name: fallbackName || '-',
          MsBankName: '-',
          AccountNumber: '-',
          AccountName: '-',
          TotalAmount: totalAmt
        }
      })

      const summaries = await Promise.all(partnerFetchPromises)
      partnerSummary.value = summaries
    } catch (err) {
      $toast.add({
        severity: 'error',
        detail: 'Gagal memuat data rekening mitra.',
        group: 'bc',
        life: 3000
      })
    } finally {
      isLoadingPartnerSummary.value = false
    }
  }

  const closeConfirmModal = () => {
    isModalOpen.value = false
  }

  const printPdf = async (data: any) => {
    store.dispatch('showLoading')
    const payload = {}
    await apiUseCase.getBlob(`/transaction/v2/FuelingPaymentBatches/${data.Id}/PaymentExport`).then((response) => {
      const { error, result } = response
      if (!error) {
        $toast.add({
          severity: 'success',
          detail: 'Data berhasil didownload',
          group: 'bc',
          closable: false,
          life: 3000
        })
        const blob = result
        const url = window.URL.createObjectURL(blob)
        const link = document.createElement('a')
        link.href = url
        link.download = `Laporan Pembayaran Batch ${data.Code}.pdf`
        document.body.appendChild(link)
        link.click()
        link.remove()
        window.URL.revokeObjectURL(url)
      } else {
        $toast.add({
          severity: 'error',
          detail: 'Data gagal didownload',
          group: 'bc',
          closable: false,
          life: 3000
        })
      }
    })
    store.dispatch('hideLoading')
  }

  const submitBatch = async (): Promise<boolean> => {
    if (selectedTransactionIds.value.size === 0) return false

    isSubmitting.value = true
    store.dispatch('showLoading')

    const payload = {
      FuelingTransactionIds: Array.from(selectedTransactionIds.value)
    }

    try {
      const response = await apiUseCase.postWithNoId('/transaction/v2/FuelingPaymentBatches', payload)
      if (!response.error) {
        const batchCode = response.result?.Code || ''
        $toast.add({
          severity: 'success',
          summary: 'Batch Pembayaran Berhasil Dibuat',
          detail: `Batch pembayaran dengan kode ${batchCode} berhasil dibuat.`,
          group: 'bc',
          life: 4000
        })
        closeConfirmModal()
        await printPdf(response.result)
        return true
      }
      $toast.add({ severity: 'error', detail: response.message, group: 'bc', life: 3000 })
      return false
    } catch (err) {
      $toast.add({
        severity: 'error',
        summary: 'Gagal membuat batch',
        detail: String(err),
        group: 'bc',
        closable: false,
        life: 3000
      })
      return false
    } finally {
      isSubmitting.value = false
      store.dispatch('hideLoading')
    }
  }

  return {
    isModalOpen,
    isLoadingPartnerSummary,
    isSubmitting,
    partnerSummary,
    grandTotal,
    openConfirmModal,
    closeConfirmModal,
    submitBatch
  }
}
